import { moment } from "@msuite/utils/date";
import { z } from "@msuite/utils/lib/zod";
import { defineMutator, type Transaction } from "@rocicorp/zero";
import { Guards } from "../handlers/guards.handlers";
import { Users } from "../handlers/users.handlers";
import { Vehicles } from "../handlers/vehicles.handlers";
import type { ZeroContext } from "../types";
import { zql } from "../zql";

/**
 * Resolve a plan-level vehicle assignment, verify its plan belongs to the caller's organisation,
 * and refuse when that day is published.
 *
 * @param tx - The Zero transaction the mutator runs in.
 * @param ctx - The server-derived caller context.
 * @param vehicleAssignmentPlanId - The assignment the mutation targets.
 * @returns The assignment row, so callers can reuse the read.
 */
async function assertAssignmentInOrg(
	tx: Transaction,
	ctx: ZeroContext,
	vehicleAssignmentPlanId: string,
) {
	const assignment = await tx.run(
		zql.vehicleAssignmentsPlan.where("id", "=", vehicleAssignmentPlanId).one(),
	);
	if (!assignment) throw new Error("Vehicle assignment not found");
	await Guards.assertPlanOnEditableDay(tx, ctx, assignment.planId);
	return assignment;
}

export const vehicleAssignmentPlanMutators = {
	insert: defineMutator(
		z.object({
			// Ids must come from the call site: the mutator runs once on the client and
			// once on the server, and a generateId() in here yields two different rows —
			// the optimistic card is then torn down and remounted when the rebase lands.
			id: z.uuid(),
			vehicleId: z.uuid(),
			planId: z.uuid(),
		}),
		async ({ args, ctx, tx }) => {
			await Guards.assertPlanOnEditableDay(tx, ctx, args.planId);
			// The day is the caller's, which says nothing about the truck parked on it: the id
			// comes from the client, and an assignment row carries no other tie to the vehicle's
			// organisation. Before the cleanup, so a refused write clears nothing off the day.
			await Guards.assertVehicleInOrg(tx, ctx, args.vehicleId);
			await Vehicles.cleanVehicleAssignments(tx, args.planId, args.vehicleId);
			return await tx.mutate.vehicleAssignmentsPlan.insert({
				id: args.id,
				createdBy: ctx.userId,
				createdAt: moment().unix() as any,
				vehicleId: args.vehicleId,
				planId: args.planId,
				userId: null,
				// The plan's organisation, which assertPlanOnEditableDay has just established is the
				// caller's, named rather than left to the column default — the guard decides the
				// tenancy, not the connection.
				orgId: ctx.orgId,
			});
		},
	),
	delete: defineMutator(
		z.object({
			vehicleAssignmentPlanId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			await assertAssignmentInOrg(tx, ctx, args.vehicleAssignmentPlanId);
			return await tx.mutate.vehicleAssignmentsPlan.delete({
				id: args.vehicleAssignmentPlanId,
			});
		},
	),
	removeUser: defineMutator(
		z.object({
			vehicleAssignmentPlanId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			await assertAssignmentInOrg(tx, ctx, args.vehicleAssignmentPlanId);
			return await tx.mutate.vehicleAssignmentsPlan.update({
				id: args.vehicleAssignmentPlanId,
				userId: null,
			});
		},
	),
	assignUser: defineMutator(
		z.object({
			vehicleAssignmentPlanId: z.uuid(),
			userId: z.string(),
		}),
		async ({ tx, args, ctx }) => {
			const assignment = await assertAssignmentInOrg(tx, ctx, args.vehicleAssignmentPlanId);
			// The day is the caller's, which says nothing about the driver: the id comes from the
			// client and the row it writes is what the publish notification reads.
			await Guards.assertUserInOrg(tx, ctx, args.userId);
			await Users.cleanUserAssignments(tx, assignment.planId, args.userId);
			return await tx.mutate.vehicleAssignmentsPlan.update({
				userId: args.userId,
				id: args.vehicleAssignmentPlanId,
			});
		},
	),
};
